import classnames from "classnames";
import { isString } from "lodash";
import React, { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import styles from "src/styles.module.scss";
import { format } from "src/utils";

const HTTPS_URL_PREFIX = "https://";
const NOT_FOUND_INDEX = -1;

const addAnchorMarkup = (textContent: string) => {
  if (!isString(textContent)) {
    return textContent;
  }

  const chunks = [];
  let end = 0;
  let begin = textContent.indexOf(HTTPS_URL_PREFIX);

  if (begin === NOT_FOUND_INDEX) {
    return textContent;
  }

  while (begin !== NOT_FOUND_INDEX) {
    chunks.push(<>{textContent.substring(end, begin)}</>);

    end = textContent.indexOf(" ", begin);
    if (end < 0) {
      end = textContent.length;
    }

    const url = textContent.substring(begin, end);
    chunks.push(
      <a href={url} target="_blank" rel="noreferrer">
        {url}
      </a>
    );

    begin = textContent.indexOf(HTTPS_URL_PREFIX, end);
  }

  return <> {chunks} </>;
};

const Field: FC<FieldDefinition<any, any>> = ({
  value,
  label,
  labelStyle,
  valueStyle,
  formatter,
  childComponentOnly,
  ChildComponent,
  childComponentProps,
}) => {
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );
  const formattedValue =
    !!formatter && !childComponentOnly ? format(value, formatter) : value;
  const OMITTED_LABELS = [
    t("aic_doc_page_agent_card_field_name"),
    t("aic_doc_page_agent_card_field_address"),
  ];
  const urlRenderedValue = addAnchorMarkup(formattedValue);
  const labelText =
    Array.isArray(labelStyle) && labelStyle.length ? (
      <span className={classnames(labelStyle)}>{label}:&nbsp;</span>
    ) : (
      <span>{label ? `${label}: ` : null}&nbsp;</span>
    );
  const valueText =
    Array.isArray(valueStyle) && valueStyle.length ? (
      <span className={classnames(valueStyle)}>{urlRenderedValue}</span>
    ) : (
      <span>{urlRenderedValue}</span>
    );

  const shouldShowLabelText =
    !!label && !childComponentOnly && !OMITTED_LABELS.includes(label);
  const shouldShowValueText = urlRenderedValue && !childComponentOnly;

  return (
    <div className={classnames(styles.display_flex, styles.align_items_center)}>
      {ChildComponent ? (
        <>
          <ChildComponent {...childComponentProps} />
          {!childComponentOnly ? <br /> : null}
        </>
      ) : null}
      {shouldShowLabelText || shouldShowValueText ? (
        <div
          className={classnames(
            styles.flex,
            styles.flex_direction_row,
            styles.flex_nowrap,
            styles.ml_8
          )}
        >
          {shouldShowLabelText ? labelText : null}
          {shouldShowValueText ? valueText : null}
        </div>
      ) : null}
    </div>
  );
};

export interface FieldDefinition<ValueType, ChildComponentPropsType> {
  value: ValueType;
  label?: string;
  valueStyle?: Array<string>;
  labelStyle?: Array<string>;
  formatter?: (value: ValueType) => ReactNode;
  ChildComponent?: FC<ChildComponentPropsType>;
  childComponentOnly?: boolean;
  childComponentProps?: ChildComponentPropsType;
}

export default Field;
